
import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { checkAndSyncEmail } from '@/utils/emailSync';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithMagicLink: (email: string, userData?: any) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: any) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Memoized auth state to prevent unnecessary re-renders
  const isAuthenticated = useMemo(() => !!user, [user]);

  const signInWithMagicLink = useCallback(async (email: string, userData?: any) => {
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectUrl,
          data: userData || {}
        }
      });
      return { error };
    } catch (error) {
      return { error };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, []);

  const updateProfile = useCallback(async (updates: any) => {
    try {
      if (!user) {
        return { error: new Error('No user logged in') };
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      return { error };
    } catch (error) {
      return { error };
    }
  }, [user]);

  useEffect(() => {
    let mounted = true;

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error);
        }
        
        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
          console.log('🔄 Auth state initialized:', session ? 'AUTHENTICATED' : 'UNAUTHENTICATED');
        }
      } catch (error) {
        console.error('Error in getInitialSession:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Set up auth state listener with optimized profile validation
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state change:', event, session ? 'AUTHENTICATED' : 'UNAUTHENTICATED');
        
        if (mounted) {
          if (session?.user) {
            // Debounced profile validation to prevent excessive database calls
            setTimeout(async () => {
              if (!mounted) return;
              
              try {
                const { data: profile, error } = await supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', session.user.id)
                  .maybeSingle();

                if (error || !profile) {
                  console.log('User profile validation failed - signing out');
                  await supabase.auth.signOut();
                  return;
                }
                
                // Optimized email sync - only when necessary
                if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
                  setTimeout(() => {
                    checkAndSyncEmail(session.user.id, session.user.email);
                  }, 1000);
                } else {
                  await checkAndSyncEmail(session.user.id, session.user.email);
                }
                
                setSession(session);
                setUser(session.user);
                
                // Optimized broadcast for email confirmation
                if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
                  try {
                    const channel = new BroadcastChannel('auth-email-change');
                    channel.postMessage({ 
                      type: 'EMAIL_CONFIRMED', 
                      userId: session.user.id,
                      newEmail: session.user.email 
                    });
                    channel.close();
                  } catch (error) {
                    // BroadcastChannel not supported, silent fail
                  }
                }
              } catch (error) {
                console.error('Error validating user:', error);
                await supabase.auth.signOut();
              }
            }, 100);
          } else {
            setSession(null);
            setUser(null);
          }
        }
      }
    );

    // Optimized BroadcastChannel listener
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('auth-email-change');
      channel.onmessage = async (event) => {
        if (event.data.type === 'EMAIL_CONFIRMED' && user) {
          const currentUser = await supabase.auth.getUser();
          if (currentUser.data.user?.email !== event.data.newEmail) {
            console.log('Email confirmed in another tab, signing out current session');
            await supabase.auth.signOut();
            window.location.reload();
          }
        }
      };
    } catch (error) {
      // BroadcastChannel not supported, silent fail
    }

    getInitialSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (channel) {
        channel.close();
      }
    };
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    session,
    loading,
    signInWithMagicLink,
    signOut,
    updateProfile,
  }), [user, session, loading, signInWithMagicLink, signOut, updateProfile]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
